/**
 * useCreateSupportTicket — F-013.
 *
 * Mutation che, se presente un allegato, prima richiede l'URL presigned e
 * carica il file su S3, poi crea il ticket (con `allegatoS3Key` se
 * l'upload è riuscito). Il toast di errore è gestito dal chiamante via
 * `errorMessageFor` così il componente può mostrare lo stato "limite
 * raggiunto" in modo dedicato (non solo un toast).
 */
import { useMutation } from '@tanstack/react-query';
import type {
  CreateSupportTicketRequest,
  SupportTicketResponse,
  SupportTicketUploadUrlRequest,
} from '@app/shared';

import {
  createSupportTicket,
  getSupportTicketUploadUrl,
  uploadSupportAttachment,
} from '../lib/support-client';

export interface CreateSupportTicketInput {
  categoria: CreateSupportTicketRequest['categoria'];
  oggetto: string;
  messaggio: string;
  praticaId?: string;
  /** Già validato lato modal contro `SUPPORT_TICKET_ATTACHMENT_CONTENT_TYPES` + dimensione. */
  allegato?: File;
}

export function useCreateSupportTicket() {
  return useMutation<SupportTicketResponse, unknown, CreateSupportTicketInput>({
    mutationFn: async (input) => {
      let allegatoS3Key: string | undefined;
      if (input.allegato) {
        const { uploadUrl, key } = await getSupportTicketUploadUrl({
          fileName: input.allegato.name,
          contentType: input.allegato.type as SupportTicketUploadUrlRequest['contentType'],
        });
        await uploadSupportAttachment(uploadUrl, input.allegato);
        allegatoS3Key = key;
      }
      return createSupportTicket({
        categoria: input.categoria,
        oggetto: input.oggetto,
        messaggio: input.messaggio,
        praticaId: input.praticaId,
        allegatoS3Key,
      });
    },
  });
}
